import { ChannelTypeEnum } from './channel.type';

/** Mirrors WalletTypeEnum in trendmulti-core (SPEC-2026-08-typed-wallets). */
export enum WalletTypeEnum {
  WHATSAPP = 'WHATSAPP',
  RCS = 'RCS',
  AI = 'AI',
}

/**
 * The only fields of a channel the wallet resolver reads — mirrors
 * BillableChannelLike in trendmulti-core (wallet.util.ts).
 */
export type BillableChannelLike = { type?: ChannelTypeEnum | string; creditLine?: boolean | null };

/** What a channel type resolves to: a wallet, no wallet, or a rule that reads the channel itself. */
type ChannelWalletResolution = WalletTypeEnum | null | ((channel: BillableChannelLike) => WalletTypeEnum | null);

/**
 * Mirrors the channel-to-wallet map of WalletTypeUtil in trendmulti-core
 * (SPEC-2026-10-whatsapp-wallet-credit-line-channels RF-01 to RF-04). The server
 * decides every charge; this copy only keeps the screens that show a price or a
 * balance for a channel giving the same answer the ledger will (RF-03).
 *
 * Every ChannelTypeEnum member is listed, the `null` ones included, so a new
 * channel type cannot ship without its billing being decided here.
 *
 * Not exported on purpose: an entry may be a rule, and a caller indexing the map
 * by type would read that rule as "billable" for every Cloud API channel.
 * `walletTypeForChannel` is the only way in.
 */
const CHANNEL_TYPE_TO_WALLET_TYPE: Record<ChannelTypeEnum, ChannelWalletResolution> = {

  [ChannelTypeEnum.RCS_API]: WalletTypeEnum.RCS,

  // Only a credit-line channel consumes from the WhatsApp wallet. The comparison
  // is strict: a channel created before the mark existed, `false`, the string
  // 'true' and 1 all stay out of the wallet (RF-02).
  [ChannelTypeEnum.CLOUD_API]: (channel) => channel.creditLine === true ? WalletTypeEnum.WHATSAPP : null,
  [ChannelTypeEnum.Z_API]: null,

  [ChannelTypeEnum.INSTAGRAM_API]: null,
  [ChannelTypeEnum.MESSENGER_API]: null,
  [ChannelTypeEnum.WEBCHAT_API]: null,

  [ChannelTypeEnum.VOIP_API]: null,
  [ChannelTypeEnum.INTEGRATIONS_API]: null,
};

/**
 * The wallet a message on this channel consumes from, or `null` when the channel
 * consumes from none. Takes the CHANNEL, never its type alone: two Cloud API
 * channels of the same company can answer differently.
 */
export function walletTypeForChannel(channel: BillableChannelLike | null | undefined): WalletTypeEnum | null {
  if (!channel?.type) return null;
  const resolution = CHANNEL_TYPE_TO_WALLET_TYPE[channel.type as ChannelTypeEnum] ?? null;
  return typeof resolution === 'function' ? resolution(channel) : resolution;
}

/**
 * Wallet families kept out of every screen, for every company — customer app and
 * admin alike.
 *
 * Empty: nothing is hidden any more. WhatsApp stayed here until a channel
 * consumed from its wallet, and left in the release that made the credit-line
 * channel do so (SPEC-2026-10-whatsapp-wallet-credit-line-channels).
 *
 * This is NOT what decides who sees the WhatsApp wallet. In the customer app
 * that is answered per company by the server — the `relevant` flag on
 * WalletSummary and WalletKpis (RF-25) — and the admin always shows every wallet
 * (RF-29). The constant and the two helpers below stay because screens in both
 * apps read them.
 */
export const HIDDEN_WALLET_TYPES: ReadonlyArray<WalletTypeEnum> = [];

/** The wallet families the UI may show, in enum order. */
export const VISIBLE_WALLET_TYPES: ReadonlyArray<WalletTypeEnum> =
  Object.values(WalletTypeEnum).filter((type) => !HIDDEN_WALLET_TYPES.includes(type));

export function isWalletTypeVisible(type: WalletTypeEnum): boolean {
  return !HIDDEN_WALLET_TYPES.includes(type);
}

/** Mirrors WalletUnitEnum in trendmulti-core (RF-03). */
export enum WalletUnitEnum {
  /** Money, in BRL — stored and served exactly as displayed (WYSIWYG). */
  BRL = 'BRL',
  /** Whole credits — AI only. */
  CREDIT = 'CREDIT',
}

/** Mirrors WalletBillingCategoryEnum in trendmulti-core (RF-50). */
export enum WalletBillingCategoryEnum {
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
  AUTHENTICATION = 'AUTHENTICATION',
  /**
   * WhatsApp — a message sent WITHOUT a template: free-form reply, reaction,
   * automated reply. Never a template's category. Recorded at zero as an
   * allowance use while the number's monthly allowance lasts, charged at this
   * price after it (SPEC-2026-10-whatsapp-wallet-credit-line-channels RF-06,
   * RF-38 to RF-40).
   */
  SERVICE = 'SERVICE',
  SINGLE = 'SINGLE',
  CONVERSATIONAL = 'CONVERSATIONAL',
}

/** Which categories each wallet family prices — drives the admin form (RF-44). */
export const WALLET_BILLING_CATEGORIES: Record<string, WalletBillingCategoryEnum[]> = {
  [WalletTypeEnum.WHATSAPP]: [
    WalletBillingCategoryEnum.MARKETING,
    WalletBillingCategoryEnum.UTILITY,
    WalletBillingCategoryEnum.AUTHENTICATION,
    WalletBillingCategoryEnum.SERVICE,
  ],
  [WalletTypeEnum.RCS]: [
    WalletBillingCategoryEnum.SINGLE,
    WalletBillingCategoryEnum.CONVERSATIONAL,
  ],
  [WalletTypeEnum.AI]: [],
};

export const WALLET_BILLING_CATEGORY_LABEL_KEY: Record<string, string> = {
  [WalletBillingCategoryEnum.MARKETING]: 'wallet.category.marketing',
  [WalletBillingCategoryEnum.UTILITY]: 'wallet.category.utility',
  [WalletBillingCategoryEnum.AUTHENTICATION]: 'wallet.category.authentication',
  [WalletBillingCategoryEnum.SERVICE]: 'wallet.category.service',
  [WalletBillingCategoryEnum.SINGLE]: 'wallet.category.single',
  [WalletBillingCategoryEnum.CONVERSATIONAL]: 'wallet.category.conversational',
};

/** The categories a WhatsApp TEMPLATE can be charged under — the ones Meta stamps on a template. */
const WHATSAPP_TEMPLATE_BILLING_CATEGORIES: ReadonlyArray<WalletBillingCategoryEnum> = [
  WalletBillingCategoryEnum.MARKETING,
  WalletBillingCategoryEnum.UTILITY,
  WalletBillingCategoryEnum.AUTHENTICATION,
];

/**
 * The billing category a send on this wallet is charged under — the web twin of
 * WalletTypeUtil.billingCategoryOf in trendmulti-core. The server decides the
 * charge; this exists so a screen that QUOTES a price before the send reads the
 * same row of the price table the ledger will.
 *
 * - WHATSAPP with a template: the template's own category when it is MARKETING,
 *   UTILITY or AUTHENTICATION, in any letter case. Anything else is `null`,
 *   which the ledger posts unpriced instead of refusing — so the screen must say
 *   "unpriced", not invent a tariff.
 * - WHATSAPP without a template: SERVICE.
 * - RCS: SINGLE, whatever is sent. CONVERSATIONAL is never chosen at send time;
 *   it is only reached when the customer replies and the session is re-priced.
 * - Anything else (AI, no wallet): `null` — not priced per send.
 *
 * `isTemplate` is required so a caller has to state it: left out, a template
 * would silently be quoted at the service price.
 */
export function walletBillingCategoryFor(
  walletType: WalletTypeEnum | string | null | undefined,
  message: { isTemplate: boolean; templateCategory?: string | null },
): WalletBillingCategoryEnum | null {
  if (walletType === WalletTypeEnum.WHATSAPP) {
    if (!message?.isTemplate) return WalletBillingCategoryEnum.SERVICE;
    const category = String(message.templateCategory ?? '').toUpperCase() as WalletBillingCategoryEnum;
    return WHATSAPP_TEMPLATE_BILLING_CATEGORIES.includes(category) ? category : null;
  }

  if (walletType === WalletTypeEnum.RCS) {
    return WalletBillingCategoryEnum.SINGLE;
  }

  return null;
}

export enum WalletEntryDirectionEnum {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}

export enum WalletEntrySourceEnum {
  ATTENDANCE_MESSAGE = 'ATTENDANCE_MESSAGE',
  CAMPAIGN_MESSAGE = 'CAMPAIGN_MESSAGE',
  CAMPAIGN_HOLD = 'CAMPAIGN_HOLD',
  FLOW_MESSAGE = 'FLOW_MESSAGE',
  SCHEDULED_MESSAGE = 'SCHEDULED_MESSAGE',
  API_MESSAGE = 'API_MESSAGE',
  CHATBOT_MESSAGE = 'CHATBOT_MESSAGE',
  INTEGRATION_MESSAGE = 'INTEGRATION_MESSAGE',
  REACTION_MESSAGE = 'REACTION_MESSAGE',
  TEMPLATE_TEST_SEND = 'TEMPLATE_TEST_SEND',
  /**
   * The re-post half of an RCS session upgrade: the customer replied, so a send
   * already charged as SINGLE was reversed and charged again as CONVERSATIONAL
   * (SPEC-2026-08-typed-wallets RF-52).
   */
  RCS_SESSION_UPGRADE = 'RCS_SESSION_UPGRADE',
  /**
   * The refund of a send the provider accepted and later reported as undelivered
   * — an RCS EXCEPTION or ERROR status callback.
   */
  RCS_DELIVERY_FAILURE = 'RCS_DELIVERY_FAILURE',
  /**
   * The same fact on WhatsApp: the refund of a send on a credit-line channel the
   * provider accepted and later reported as failed
   * (SPEC-2026-10-whatsapp-wallet-credit-line-channels RF-21). Its own source so
   * the statement can filter by it (RF-24).
   */
  WHATSAPP_DELIVERY_FAILURE = 'WHATSAPP_DELIVERY_FAILURE',
  AI_AGENT = 'AI_AGENT',
  PURCHASE_ASAAS = 'PURCHASE_ASAAS',
  MANUAL_LOAD = 'MANUAL_LOAD',
  REVERSAL = 'REVERSAL',
  RECONCILIATION_ADJUSTMENT = 'RECONCILIATION_ADJUSTMENT',
  MIGRATION_OPENING = 'MIGRATION_OPENING',
}

/**
 * Mirrors WalletBlockedReasonEnum in trendmulti-core — why a send was refused,
 * persisted on Message.metadata.billing.
 */
export enum WalletBlockedReasonEnum {
  WALLET_INSUFFICIENT = 'WALLET_INSUFFICIENT',
  WALLET_UNAVAILABLE = 'WALLET_UNAVAILABLE',
  /**
   * The RCS service session has elapsed and the provider will not deliver
   * (SPEC-2026-08-rcs-inbound-status-and-session, RF-15). Not a balance
   * problem: the send is refused however much credit the company holds.
   */
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

/** i18n key for a wallet family label. Values are brand names, never translated. */
export const WALLET_TYPE_LABEL_KEY: Record<string, string> = {
  [WalletTypeEnum.WHATSAPP]: 'wallet.type.whatsapp',
  [WalletTypeEnum.RCS]: 'wallet.type.rcs',
  [WalletTypeEnum.AI]: 'wallet.type.ai',
};

export const WALLET_SOURCE_LABEL_KEY: Record<string, string> = {
  [WalletEntrySourceEnum.ATTENDANCE_MESSAGE]: 'wallet.ledger.sourceAttendanceMessage',
  [WalletEntrySourceEnum.CAMPAIGN_MESSAGE]: 'wallet.ledger.sourceCampaign',
  [WalletEntrySourceEnum.CAMPAIGN_HOLD]: 'wallet.ledger.sourceCampaignHold',
  [WalletEntrySourceEnum.FLOW_MESSAGE]: 'wallet.ledger.sourceFlow',
  [WalletEntrySourceEnum.SCHEDULED_MESSAGE]: 'wallet.ledger.sourceScheduled',
  [WalletEntrySourceEnum.API_MESSAGE]: 'wallet.ledger.sourceApi',
  [WalletEntrySourceEnum.CHATBOT_MESSAGE]: 'wallet.ledger.sourceChatbot',
  [WalletEntrySourceEnum.INTEGRATION_MESSAGE]: 'wallet.ledger.sourceIntegration',
  [WalletEntrySourceEnum.REACTION_MESSAGE]: 'wallet.ledger.sourceReaction',
  [WalletEntrySourceEnum.TEMPLATE_TEST_SEND]: 'wallet.ledger.sourceTemplateTest',
  [WalletEntrySourceEnum.RCS_SESSION_UPGRADE]: 'wallet.ledger.sourceRcsSessionUpgrade',
  [WalletEntrySourceEnum.RCS_DELIVERY_FAILURE]: 'wallet.ledger.sourceRcsDeliveryFailure',
  // Shares the RCS key on purpose: its text names no channel in any locale
  // ("Delivery failure refund"), and a statement is always one wallet's, so the
  // provider is never in doubt. Only the key's name is historical.
  [WalletEntrySourceEnum.WHATSAPP_DELIVERY_FAILURE]: 'wallet.ledger.sourceRcsDeliveryFailure',
  [WalletEntrySourceEnum.AI_AGENT]: 'wallet.ledger.sourceAi',
  [WalletEntrySourceEnum.PURCHASE_ASAAS]: 'wallet.ledger.sourcePurchase',
  [WalletEntrySourceEnum.MANUAL_LOAD]: 'wallet.ledger.sourceManualLoad',
  [WalletEntrySourceEnum.REVERSAL]: 'wallet.ledger.sourceReversal',
  [WalletEntrySourceEnum.RECONCILIATION_ADJUSTMENT]: 'wallet.ledger.sourceAdjustment',
  [WalletEntrySourceEnum.MIGRATION_OPENING]: 'wallet.ledger.sourceOpening',
};
